/*
  sidebar の PortfolioSwitcher で portfolio の切り替え、検索、新規作成、並び替えを扱う。
*/

import { updateSetupPortfolioCachedPortfolios } from '@/features/portfolio/api/setupPortfolio'
import { updatePortfolioDisplayOrder } from '@/features/portfolio/api/updatePortfolioDisplayOrder'
import { registerNewPortfolio } from '@/features/portfolio/logic/registerNewPortfolio'
import {
  type PortfolioMove,
  findPortfolioMove,
  reorderPortfoliosByDrag,
} from '@/features/portfolio/logic/reorderPortfolioDisplayOrder'
import { activeWriteOperationAtom } from '@/features/portfolio/states/writeOperation'
import useDebounce from '@/hooks/useDebounce'
import { useToast } from '@/hooks/useToast'
import type { Portfolio } from '@/types'
import { useAuth } from '@clerk/clerk-react'
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useAtomValue } from 'jotai'
import {
  ChevronsUpDown,
  GripVertical,
  LoaderCircle,
  Plus,
  Search,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdownMenu'
import { SidebarMenuButton } from './ui/sidebar'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'

type PortfolioTab = 'active' | 'archive'

/**
 * sidebar の PortfolioSwitcher 本体。
 */
export const PortfolioSwitcher = (props: {
  portfolio: Portfolio
  portfolios: readonly Portfolio[]
}) => {
  const { portfolio, portfolios } = props
  const { getToken } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  const { location } = useRouterState()
  const activeWriteOperation = useAtomValue(activeWriteOperationAtom)
  const writeOperationRunning = activeWriteOperation !== null
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  const [orderedPortfolios, setOrderedPortfolios] = useState(portfolios)
  const isPersistingOrderRef = useRef(false)

  /**
   * 選択中 portfolio を切り替える。
   */
  const changePortfolio = async (uuid: string) => {
    if (portfolio.uuid === uuid || writeOperationRunning) {
      return
    }
    await navigate({ to: location.pathname, search: { id: uuid } })
    window.location.reload()
  }

  const [isCreating, setIsCreating] = useState(false)
  const [isReordering, setIsReordering] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [tab, setTab] = useState<PortfolioTab>('active')

  useEffect(() => {
    if (isPersistingOrderRef.current) {
      return
    }

    setOrderedPortfolios(portfolios)
  }, [portfolios])

  const filteredPortfolios = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase()
    if (normalizedKeyword.length === 0) {
      return orderedPortfolios
    }

    return orderedPortfolios.filter((p) => {
      return (
        p.name.toLowerCase().includes(normalizedKeyword) ||
        p.uuid.toLowerCase().includes(normalizedKeyword)
      )
    })
  }, [keyword, orderedPortfolios])
  const activePortfolios = useMemo(() => {
    return filteredPortfolios.filter((p) => !p.archive)
  }, [filteredPortfolios])
  const archivePortfolios = useMemo(() => {
    return filteredPortfolios.filter((p) => p.archive)
  }, [filteredPortfolios])
  const visiblePortfolios =
    tab === 'active' ? activePortfolios : archivePortfolios

  /**
   * 並び替え後の順序を API と setup cache に反映する。
   */
  const persistPortfolioOrder = async (params: {
    move: PortfolioMove
    nextPortfolios: readonly Portfolio[]
    previousPortfolios: readonly Portfolio[]
  }) => {
    const { move, nextPortfolios, previousPortfolios } = params

    setOrderedPortfolios(nextPortfolios)
    updateSetupPortfolioCachedPortfolios({ portfolios: nextPortfolios })
    isPersistingOrderRef.current = true
    setIsReordering(true)
    try {
      await updatePortfolioDisplayOrder({
        getToken,
        movedPortfolioUuid: move.movedPortfolioUuid,
        previousPortfolioUuid: move.previousPortfolioUuid,
        nextPortfolioUuid: move.nextPortfolioUuid,
      })
    } catch (error) {
      setOrderedPortfolios(previousPortfolios)
      updateSetupPortfolioCachedPortfolios({ portfolios: previousPortfolios })
      toast({
        variant: 'destructive',
        title: 'Portfolio の並び順を保存できませんでした',
        description:
          error instanceof Error
            ? error.message
            : '時間をおいて再試行してください。',
      })
    } finally {
      isPersistingOrderRef.current = false
      setIsReordering(false)
    }
  }

  /**
   * ドラッグ完了時に表示中 subset の順序変更を永続化する。
   */
  const handleDragEnd = async (event: DragEndEvent) => {
    if (writeOperationRunning) {
      return
    }
    const { active, over } = event
    if (over === null || active.id === over.id) {
      return
    }

    const previousPortfolios = orderedPortfolios
    const nextPortfolios = reorderPortfoliosByDrag({
      portfolios: previousPortfolios,
      visiblePortfolios,
      activePortfolioUuid: String(active.id),
      overPortfolioUuid: String(over.id),
    })
    if (nextPortfolios === previousPortfolios) {
      return
    }

    const move = findPortfolioMove({
      movedPortfolioUuid: String(active.id),
      portfolios: nextPortfolios,
    })

    await persistPortfolioOrder({ move, nextPortfolios, previousPortfolios })
  }

  /**
   * 新しい portfolio を作成して onboarding へ遷移する。
   */
  const createAndChangePortfolio = async (event: React.MouseEvent) => {
    event.preventDefault()
    if (writeOperationRunning) {
      return
    }
    setIsCreating(true)
    try {
      const newPortfolio = await registerNewPortfolio({
        portfolioCount: portfolios.length,
        getToken,
      })
      await navigate({
        to: '/onboarding',
        search: { id: newPortfolio.uuid },
      })
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={writeOperationRunning}>
        <SidebarMenuButton
          size="lg"
          asChild
          title={
            writeOperationRunning
              ? '更新処理中はポートフォリオを切り替えられません'
              : undefined
          }
        >
          <div className="flex cursor-pointer items-center">
            <img
              src="/icon.svg"
              alt="Cryptorch"
              className="h-9 w-9 rounded-md"
            />
            <div className="flex flex-1 flex-col overflow-x-hidden">
              <p className="overflow-x-hidden text-ellipsis text-nowrap font-medium">
                {portfolio.name}
              </p>
              <p className="mt-0.5 text-muted-foreground text-xs">
                ID: {portfolio.uuid}
              </p>
            </div>
            <ChevronsUpDown className="h-4 w-4" />
          </div>
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="mt-2 min-w-56 max-w-sm">
        <div className="mb-2 flex items-center border-b px-2 py-1.5">
          <Search className="mr-2 h-3.5 w-3.5" />
          <DebounceInput keyword={keyword} setKeyword={setKeyword} />
        </div>
        <Tabs
          value={tab}
          onValueChange={(value) => setTab(value as PortfolioTab)}
        >
          <TabsList className="mb-1 grid w-full grid-cols-2">
            <TabsTrigger value="active">active</TabsTrigger>
            <TabsTrigger value="archive">archive</TabsTrigger>
          </TabsList>
          <TabsContent value="active" className="mt-0">
            <SortablePortfolioList
              currentPortfolioUuid={portfolio.uuid}
              isReordering={isReordering}
              portfolios={activePortfolios}
              sensors={sensors}
              onChangePortfolio={changePortfolio}
              onDragEnd={handleDragEnd}
            />
          </TabsContent>
          <TabsContent value="archive" className="mt-0">
            <SortablePortfolioList
              currentPortfolioUuid={portfolio.uuid}
              isReordering={isReordering}
              portfolios={archivePortfolios}
              sensors={sensors}
              onChangePortfolio={changePortfolio}
              onDragEnd={handleDragEnd}
            />
          </TabsContent>
        </Tabs>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={createAndChangePortfolio}
          disabled={isCreating || writeOperationRunning}
        >
          {isCreating ? (
            <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          <span>Create Portfolio</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const SortablePortfolioList = (props: {
  currentPortfolioUuid: string
  isReordering: boolean
  portfolios: readonly Portfolio[]
  sensors: ReturnType<typeof useSensors>
  onChangePortfolio: (uuid: string) => Promise<void>
  onDragEnd: (event: DragEndEvent) => Promise<void>
}) => {
  const {
    currentPortfolioUuid,
    isReordering,
    portfolios,
    sensors,
    onChangePortfolio,
    onDragEnd,
  } = props

  if (portfolios.length === 0) {
    return (
      <div className="flex w-56 justify-center rounded bg-muted px-2.5 py-2 text-muted-foreground text-xs">
        キーワードに一致するポートフォリオがありません
      </div>
    )
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={portfolios.map((item) => item.uuid)}
        strategy={verticalListSortingStrategy}
      >
        <div className="max-h-[33vh] overflow-y-scroll">
          {portfolios.map((item) => (
            <SortablePortfolioListItem
              key={item.uuid}
              currentPortfolioUuid={currentPortfolioUuid}
              isDisabled={isReordering}
              portfolio={item}
              onChangePortfolio={onChangePortfolio}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  )
}

const SortablePortfolioListItem = (props: {
  currentPortfolioUuid: string
  isDisabled: boolean
  portfolio: Portfolio
  onChangePortfolio: (uuid: string) => Promise<void>
}) => {
  const { currentPortfolioUuid, isDisabled, portfolio, onChangePortfolio } =
    props
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: portfolio.uuid,
    disabled: isDisabled,
  })

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={[
        'mb-1 flex items-center gap-1 rounded-sm px-1 py-0.5',
        isDragging ? 'z-10 bg-accent shadow-sm' : '',
        portfolio.uuid === currentPortfolioUuid ? 'bg-accent/60' : '',
      ].join(' ')}
    >
      <button
        type="button"
        className="flex h-8 w-7 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
        disabled={isDisabled}
        onClick={(event) => event.stopPropagation()}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        className="flex min-w-0 flex-1 flex-col rounded-sm px-2 py-1 text-left text-sm transition-colors hover:bg-accent"
        onClick={() => void onChangePortfolio(portfolio.uuid)}
      >
        <span className="truncate font-medium">{portfolio.name}</span>
        <span className="mt-0.5 truncate text-muted-foreground text-xs">
          ID: {portfolio.uuid}
        </span>
      </button>
      {isDisabled && portfolio.uuid === currentPortfolioUuid ? (
        <LoaderCircle className="mr-1 h-3.5 w-3.5 animate-spin text-muted-foreground" />
      ) : null}
    </div>
  )
}

const DebounceInput = (props: {
  keyword: string
  setKeyword: (keyword: string) => void
}) => {
  const { keyword, setKeyword } = props
  const [value, setValue] = useState(keyword)

  useEffect(() => {
    setValue(keyword)
  }, [keyword])

  const { debounce, flush } = useDebounce(500)

  const handleChange = (nextKeyword: string) => {
    debounce(() => {
      setKeyword(nextKeyword)
    })
    setValue(nextKeyword)
  }

  return (
    <input
      type="text"
      className="flex flex-1 rounded-md bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
      placeholder="名前・IDで検索"
      value={value}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={() => flush()}
    />
  )
}

export default PortfolioSwitcher
